// src/services/callRender.js
//
// 🟢 A faithful port of the app's screens/callBubbleRender.ts — the single source of
//    truth for what a call entry SAYS. The outcome stored on disk is identical on both
//    sides; the TEXT flips on `isMyCall` so caller and callee each get the right
//    perspective. Getting this wrong is worse than not rendering calls at all: showing
//    "Missed" to the person who cancelled is an actively false statement about someone's
//    behaviour, so the mapping below is copied case for case rather than approximated.
//
// Call docs are PLAINTEXT (`callData` is written unencrypted and every reader consumes
// it directly), so the web needs no key material for any of this.

export const MISSED_RED = '#FF3B30';
export const FAILED_GREY = '#9E9E9E';

/** M:SS — no hours bucket, exactly like the app (and WhatsApp). */
export function formatCallDuration(seconds) {
  const s = Number(seconds);
  if (!s || s < 0 || !Number.isFinite(s)) return '0:00';
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

/**
 * @returns {{ icon, statusText, displayDuration, statusColor, noteText }}
 * `icon` is one of: phone-outgoing | phone-incoming | phone-missed | phone-off |
 *                   video-off | video | phone
 */
export function renderCallBubble({ outcome, isVideo, isMyCall, duration, handledOnOtherDevice, handledOnWeb }) {
  const callLabel = isVideo ? 'Video call' : 'Voice call';
  const lower = callLabel.toLowerCase();
  // 🟢 "Answered from the web" outranks the generic "another device" wording —
  //    when the server tagged the answering device as a browser, say so.
  const note = handledOnWeb && outcome === 'answered'
    ? 'Answered from the web'
    : handledOnOtherDevice
      ? (outcome === 'answered' ? 'Answered on another device'
        : outcome === 'declined' ? 'Declined on another device' : null)
      : null;
  const w = (o) => ({ ...o, noteText: note });

  switch (outcome) {
    case 'answered':
      return w({
        icon: isMyCall ? 'phone-outgoing' : 'phone-incoming',
        statusText: callLabel,
        displayDuration: formatCallDuration(duration),
        statusColor: null,
      });

    case 'missed':
      // caller side: "missed" means they never picked up
      if (isMyCall) {
        return w({ icon: isVideo ? 'video-off' : 'phone-off', statusText: 'Call not answered', displayDuration: null, statusColor: null });
      }
      return w({ icon: isVideo ? 'video-off' : 'phone-missed', statusText: `Missed ${lower}`, displayDuration: null, statusColor: MISSED_RED });

    case 'cancelled':
      if (isMyCall) {
        return w({ icon: isVideo ? 'video-off' : 'phone-off', statusText: 'Cancelled', displayDuration: null, statusColor: null });
      }
      // the callee only saw it ring and stop — from their side that IS a missed call
      return w({ icon: isVideo ? 'video-off' : 'phone-missed', statusText: `Missed ${lower}`, displayDuration: null, statusColor: MISSED_RED });

    case 'declined':
      return w({
        icon: isVideo ? 'video-off' : 'phone-off',
        statusText: isMyCall ? 'Declined' : 'You declined',
        displayDuration: null,
        statusColor: null,
      });

    case 'busy':
      if (isMyCall) {
        return w({ icon: isVideo ? 'video-off' : 'phone-off', statusText: 'Busy', displayDuration: null, statusColor: null });
      }
      return w({ icon: isVideo ? 'video-off' : 'phone-missed', statusText: 'Missed (busy)', displayDuration: null, statusColor: MISSED_RED });

    case 'failed':
      return w({ icon: isVideo ? 'video-off' : 'phone-off', statusText: 'Call failed', displayDuration: null, statusColor: FAILED_GREY });

    default:
      return w({ icon: 'phone', statusText: callLabel, displayDuration: null, statusColor: null });
  }
}

/** A group call: who started what, how long it ran, and from which device.
 *
 * 🕒 IT USED TO RETURN `displayDuration: null` and say in a comment that group
 *    calls carry no duration. They carried none because nothing ever measured
 *    one — the phone wrote a literal `duration: 0` — so the row here rendered
 *    "0:00" the moment anything did start showing it. Both clients now measure
 *    against `activeCall.startedAt`, the server's single stamp, so this renders
 *    a real length; a call that produced none still renders nothing rather than
 *    a confident zero. */
export function renderGroupCallBubble({ isVideo, didIStart, starterName, duration, startedFrom }) {
  const kind = isVideo ? 'video call' : 'voice call';
  const who = didIStart ? 'You' : (starterName || 'Member');
  return {
    icon: isVideo ? 'video' : 'phone',
    statusText: `${who} started a ${kind}`,
    displayDuration: duration > 0 ? formatCallDuration(duration) : null,
    statusColor: null,
    /* 🟥 ONLY THE PERSON WHO PLACED IT NEEDS THIS. The note answers "which of my
       devices did I call from" — a question nobody else in the group is asking,
       and telling them is a detail about someone's setup rather than about the
       call. Everyone still sees the call itself. */
    noteText: (startedFrom === 'web' && didIStart) ? 'from the web' : null,
  };
}

/** One line for the chat list. */
export function renderCallPreviewText(p) {
  const c = p.isGroup ? renderGroupCallBubble(p) : renderCallBubble(p);
  /* a group call has no `outcome` to be 'answered' — it either ran for a while
     or it did not, and the duration is the only thing that says which */
  const showTail = c.displayDuration && (p.isGroup || p.outcome === 'answered');
  return showTail ? `${c.statusText} · ${c.displayDuration}` : c.statusText;
}

/** Pull the render inputs out of a message doc (or a chat-doc lastMessageSystem). */
export function callInputs(src, myUid) {
  const d = (src && (src.callData || src)) || {};
  const initiator = d.callInitiatedBy || src?.senderId || null;
  return {
    outcome: d.outcome || (d.answered ? 'answered' : 'missed'),
    isVideo: d.callType === 'video',
    isMyCall: !!initiator && initiator === myUid,
    duration: Number(d.duration) || 0,
    handledOnOtherDevice: !!d.handledOnOtherDevice,
    // 🟢 the callee's end of this call was a browser — "Answered from the web"
    handledOnWeb: !!d.handledOnWeb,
    /* 🟥 `isGroup` LIVES IN TWO PLACES AND THIS ONLY READ ONE OF THEM. `d` is
       `callData`, but every writer — this browser, the phone's
       saveGroupCallHistory, the server's record — puts `isGroup: true` at the
       TOP level of the message, as a SIBLING of callData. So `d.isGroup` was
       always undefined and the whole test fell through to the participant
       count, which says "group" only above two people.

       A two-person group call therefore rendered as a one-to-one call: the
       wrong sentence ("Video call" instead of "Abdullah started a video call"),
       no side, no in-call chat preview, and no way to click through to what was
       said — because every one of those is gated on `isGroup`. A three-person
       call escaped it by accident. `src` is the message; read both. */
    isGroup: !!d.isGroup || !!(src && src.isGroup)
      || (Array.isArray(d.participants) && d.participants.length > 2),
    /* the thread anything said during the call was written to, and the device
       that placed it — both ride on the record the host wrote */
    callSessionId: d.callSessionId || null,
    startedFrom: d.startedFrom === 'web' ? 'web' : null,
    didIStart: !!initiator && initiator === myUid,
    starterName: d.senderName || src?.senderName || null,
    callType: d.callType || 'voice',
  };
}

export const isCallMessage = (m) => m?.systemType === 'call_history' || m?.type === 'missed_call';
